import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

/** Toast API provided by {@link ToastProvider}; read it with `useToast()`. */
export const ToastContext = createContext(null);

/** How long each severity stays on screen before closing itself. */
const AUTO_HIDE_MS = {
  success: 4000,
  info: 4000,
  warning: 5000,
  error: 6000,
};

/** Colour palette for each toast severity (same tones as the club frontend). */
const TONES = {
  success: { bg: "#f3f9e6", border: "#cfe59a", text: "#3f5a00", icon: "#5b8a00" },
  info: { bg: "#eef7f3", border: "#bfe3d3", text: "#0b5a3a", icon: "#0b7a4f" },
  warning: { bg: "#fff8e7", border: "#f2d48a", text: "#7f5b00", icon: "#b07d00" },
  error: { bg: "#fdecec", border: "#f5c2c2", text: "#8a1c1c", icon: "#c62828" },
};

/**
 * App-wide toast queue, rendered with MUI Snackbar + Alert.
 * @param children - Subtree that gets access to the toast API via context.
 */
export function ToastProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  // True between a toast starting to close and its exit animation finishing.
  const [exiting, setExiting] = useState(false);
  const nextId = useRef(0);

  const current = queue[0];

  // Show the head of the queue whenever nothing is on screen.
  useEffect(() => {
    if (current && !open && !exiting) setOpen(true);
  }, [current, open, exiting]);

  /** Queues a toast, skipping it if an identical one is already showing or queued. */
  const push = useCallback((severity, message) => {
    setQueue((q) =>
      q.some((toast) => toast.severity === severity && toast.message === message)
        ? q
        : [...q, { id: nextId.current++, severity, message }]
    );
  }, []);

  const api = useMemo(
    () => ({
      success: (message) => push("success", message),
      info: (message) => push("info", message),
      warning: (message) => push("warning", message),
      error: (message) => push("error", message),
    }),
    [push]
  );

  /** Starts closing the current toast, ignoring dismissals caused by clicking away. */
  function handleClose(_event, reason) {
    if (reason === "clickaway") return;
    setExiting(true);
    setOpen(false);
  }

  /** Removes the just-closed toast from the queue once its exit animation finishes. */
  function handleExited() {
    setQueue((q) => q.slice(1));
    setExiting(false);
  }

  const tone = current ? TONES[current.severity] : null;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {current && tone && (
        <Snackbar
          key={current.id}
          open={open}
          autoHideDuration={AUTO_HIDE_MS[current.severity]}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionProps={{ onExited: handleExited }}
          sx={{ top: { xs: 70, sm: 72 } }}
        >
          <Alert
            severity={current.severity}
            variant="standard"
            onClose={handleClose}
            sx={{
              minWidth: { sm: 320 },
              maxWidth: 440,
              alignItems: "center",
              borderRadius: "11px",
              border: `1px solid ${tone.border}`,
              backgroundColor: tone.bg,
              color: tone.text,
              boxShadow: "0 8px 24px rgba(10, 26, 16, .12)",
              fontSize: 13,
              fontWeight: 500,
              "& .MuiAlert-icon": { color: tone.icon, opacity: 1 },
              "& .MuiAlert-action": { color: tone.text },
            }}
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
}
